import { getUserData } from "@/auth/getUserData";
import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import TypingIndicatorInterface from "@/interfaces/inbox/typingIndicator.interface";
import { useWebsockets } from "@/libs/websockets";
import { useChatsStore } from "@/store/useChatsStore";
import {
    BaseWebsocketEvent,
    Chat,
    WebsocketTypingStartEvent,
    WebsocketTypingStopEvent,
} from "@saysimple-platform/dtos";
import { readonly, ref, Ref } from "@vue/composition-api";

const state: Ref<TypingIndicatorInterface[]> = ref([
    // For testing purposes, uncomment this
    //{
    //    chatId: 214,
    //    userId: 6,
    //    name: "Test",
    //},
]);

// @ts-ignore
export default function useTypingIndicators(websockets?: useWebsockets) {
    const clearTypingIndicators = (): void => {
        state.value = [];
    };

    const addTypingIndicator = (payload: WebsocketTypingStartEvent): void => {
        // The uniqueness is the combination of chatId and userId.
        const entryExists = state.value.some(
            (item) => item.chatId === payload.chatId && item.userId === payload.userId
        );

        if (entryExists) {
            return;
        }

        state.value.push({
            chatId: payload.chatId,
            userId: payload.userId,
            name: payload.userName,
        });
    };

    const removeTypingIndicator = (payload: WebsocketTypingStopEvent): void => {
        const typingIndicatorsIndex: number = state.value.findIndex(
            (item: TypingIndicatorInterface) => item.chatId === payload.chatId && item.userId === payload.userId
        );

        if (typingIndicatorsIndex === -1) {
            return;
        }

        state.value.splice(typingIndicatorsIndex, 1);
    };

    // Resolve the complete list to a directly to use indicator for a specific chat
    const resolveTypingIndicatorsForChat = (chatId: Chat["id"]): { count: number; names: string } => {
        const indicators = state.value.filter((typingIndicator) => typingIndicator.chatId === chatId);

        return {
            names: indicators.map((typingIndicator) => typingIndicator.name).join(", "),
            count: indicators.length,
        };
    };

    //--------------------------------------------------------------------------

    let shouldSendStartTyping = true; // Flag variable to track sending start typing message
    let lastSentTimestamp = 0; // Timestamp of the last sent message
    const messageCooldown = 5000; // Time in milliseconds to wait before sending another message
    let typingTimer: NodeJS.Timeout;

    const isTyping = ref(false);
    const startDebounceInterval = 500;
    const stopDebounceInterval = 5000;
    const chatsStore = useChatsStore();
    const { inboxModuleType } = useInboxModuleType();

    const startTypingIndicator = (): void => {
        clearTimeout(typingTimer);

        isTyping.value = true;
        const currentTime = Date.now();

        if (shouldSendStartTyping && currentTime - lastSentTimestamp >= messageCooldown) {
            shouldSendStartTyping = false;

            setTimeout(() => {
                websockets.sendWebsocketsMessage({
                    type: "TYPING_START",
                    payload: {
                        inboxModuleType: inboxModuleType.value,
                        chatId: chatsStore.activeChat?.chat.id,
                        userId: getUserData().id,
                        userName: getUserData().name,
                        typingTime: new Date() as unknown as string,
                        direction: "out",
                        type: "messenger",
                    } as WebsocketTypingStartEvent,
                } as BaseWebsocketEvent);

                shouldSendStartTyping = true;
                lastSentTimestamp = Date.now();
            }, startDebounceInterval);
        }
    };

    const stopTypingIndicator = (): void => {
        clearTimeout(typingTimer);
        isTyping.value = false;

        // Start the timer to send the WebSocket message after the debounce interval
        typingTimer = setTimeout(() => {
            websockets.sendWebsocketsMessage({
                type: "TYPING_STOP",
                payload: {
                    chatId: chatsStore.activeChat?.chat.id,
                    inboxModuleType: inboxModuleType.value,
                    userId: getUserData().id,
                    userName: getUserData().name,
                } as WebsocketTypingStopEvent,
            } as BaseWebsocketEvent);
        }, stopDebounceInterval);

        lastSentTimestamp = Date.now();
    };

    return {
        addTypingIndicator,
        clearTypingIndicators,
        removeTypingIndicator,
        resolveTypingIndicatorsForChat,
        startTypingIndicator,
        stopTypingIndicator,
        typingIndicators: readonly(state),
    };
}
